import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle, Loader2, ImagePlus, MoreVertical, Flag, Ban } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { sendOrQueue } from "@/lib/outbox";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ReportDialog } from "@/components/ReportDialog";
import { MessageReactions, toggleReaction } from "@/components/MessageReactions";

// Only render image URLs that are absolute https links. Defense-in-depth
// against stored XSS: the server validates the URL at ingestion, but rows
// persisted before that fix (or any non-https/javascript: value) must never
// reach an <a href>/<img src>.
function isSafeImageUrl(url?: string | null): boolean {
  if (!url) return false;
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}

type ChatMessage = {
  id: string;
  orderId: string;
  senderId: string;
  recipientId: string;
  body: string;
  imageUrl: string | null;
  isRead: number;
  reactions: Record<string, string> | null;
  createdAt: string | null;
  senderName: string | null;
};

type MessagesResponse = {
  messages: ChatMessage[];
  allowImages: boolean;
};

type OrderChatProps = {
  orderId: string;
  otherPartyName: string;
  recipientId?: string;
};

function formatTime(dateStr: string | null) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function OrderChat({ orderId, otherPartyName, recipientId }: OrderChatProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [safetyMenuOpen, setSafetyMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const handleBlock = async () => {
    if (!recipientId) return;
    setSafetyMenuOpen(false);
    try {
      await apiRequest("POST", "/api/block", { userId: recipientId });
      toast({
        title: `Blocked ${otherPartyName}`,
        description: "They can no longer message you, and you won't see their content.",
      });
    } catch (e: any) {
      toast({
        title: "Couldn't block",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const prevCountRef = useRef(0);

  const userId = user?.id;

  const { data, isLoading } = useQuery<MessagesResponse>({
    queryKey: ["/api/orders", orderId, "messages"],
    queryFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/messages`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    refetchInterval: 15000,
    enabled: !!orderId,
  });

  const allMessages = data?.messages ?? [];
  const allowImages = data?.allowImages ?? false;

  // Multi-seller orders can have messages between the buyer and several
  // different sellers sharing the same orderId. When recipientId is given,
  // only show messages whose sender/recipient pair is {user, counterparty}.
  const messages = useMemo(() => {
    if (!recipientId || !userId) return allMessages;
    return allMessages.filter(
      (m) =>
        (m.senderId === userId && m.recipientId === recipientId) ||
        (m.senderId === recipientId && m.recipientId === userId),
    );
  }, [allMessages, recipientId, userId]);

  // Index of the most recent message the current user sent — the only place we
  // show a read receipt (iMessage-style), so the thread doesn't get noisy.
  const lastOwnIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].senderId === userId) return i;
    }
    return -1;
  }, [messages, userId]);

  // Read-receipt: fire once when the chat is opened for this orderId. Sending
  // a message from within the open chat no longer auto-clears its own
  // unread-on-the-other-side signal — opening the chat is the only thing
  // that marks messages read, matching standard messaging semantics.
  // For multi-seller orders, the server scopes the read-receipt to messages
  // from this specific counterparty so opening seller A's chat doesn't
  // silently clear unread from seller B.
  useEffect(() => {
    if (!orderId) return;
    fetch(`/api/orders/${orderId}/messages/mark-read`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(recipientId ? { counterpartyId: recipientId } : {}),
    })
      .then((res) => {
        if (!res.ok) return;
        queryClient.invalidateQueries({ queryKey: ["/api/my/unread-messages"] });
        queryClient.invalidateQueries({ queryKey: ["/api/my/unread-messages-by-order"] });
        queryClient.invalidateQueries({ queryKey: ["/api/my/conversations"] });
      })
      .catch(() => {});
  }, [orderId, recipientId, queryClient]);

  const messagesKey = ["/api/orders", orderId, "messages"];

  const sendMutation = useMutation({
    mutationFn: (input: { body: string; imageUrl?: string }) => {
      const body: Record<string, unknown> = { body: input.body };
      if (input.imageUrl) body.imageUrl = input.imageUrl;
      if (recipientId) body.recipientId = recipientId;
      return sendOrQueue({
        kind: "chat",
        label: "Message",
        url: `/api/orders/${orderId}/messages`,
        method: "POST",
        body,
      });
    },
    // Show the message immediately so it never "disappears" when queued in a
    // dead zone — the outbox sends it on reconnect and the sync invalidation
    // below replaces this optimistic copy with the server's.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: messagesKey });
      const prev = queryClient.getQueryData<MessagesResponse>(messagesKey);
      const tempId = `pending-${Date.now()}`;
      if (prev && userId) {
        const optimistic: ChatMessage = {
          id: tempId,
          orderId,
          senderId: userId,
          recipientId: recipientId ?? "",
          body: input.body,
          imageUrl: input.imageUrl ?? null,
          isRead: 1,
          reactions: null,
          createdAt: new Date().toISOString(),
          senderName: null,
        };
        queryClient.setQueryData<MessagesResponse>(messagesKey, { ...prev, messages: [...prev.messages, optimistic] });
      }
      return { prev };
    },
    onSuccess: (result) => {
      if (result === "queued") {
        toast({ title: "Saved offline", description: "Your message will send when you're back in service." });
        return;
      }
      queryClient.invalidateQueries({ queryKey: messagesKey });
      queryClient.invalidateQueries({ queryKey: ["/api/my/unread-messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/unread-messages-by-order"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/conversations"] });
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(messagesKey, ctx.prev);
      toast({ title: "Message not sent", description: "Try again.", variant: "destructive" });
    },
  });

  // When the outbox drains a queued message, pull the real thread so the
  // optimistic copy is replaced by the server's.
  useEffect(() => {
    const onSync = () => queryClient.invalidateQueries({ queryKey: messagesKey });
    window.addEventListener("stoop-outbox-sync", onSync);
    return () => window.removeEventListener("stoop-outbox-sync", onSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, queryClient]);

  const reactMutation = useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: string; emoji: string }) => {
      const res = await apiRequest("POST", `/api/messages/${messageId}/react`, { emoji });
      return res.json();
    },
    onMutate: async ({ messageId, emoji }) => {
      const key = ["/api/orders", orderId, "messages"];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<MessagesResponse>(key);
      if (prev && userId) {
        queryClient.setQueryData<MessagesResponse>(key, {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === messageId
              ? { ...m, reactions: toggleReaction(m.reactions, userId, emoji) }
              : m,
          ),
        });
      }
      return { prev, key };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders", orderId, "messages"] });
    },
  });

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sendMutation.isPending) return;
    setText("");
    sendMutation.mutate({ body: trimmed });
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch("/api/uploads/item-image", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const { imageUrl } = (await res.json()) as { imageUrl: string };
      await sendMutation.mutateAsync({ body: text.trim(), imageUrl });
      setText("");
    } catch {
      // Surface nothing intrusive; the chat just won't show the image.
    } finally {
      setIsUploading(false);
    }
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > prevCountRef.current) {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: prevCountRef.current === 0 ? "instant" : "smooth",
      });
    }
    prevCountRef.current = messages.length;
  }, [messages.length]);

  return (
    <div className="flex flex-col rounded-lg border bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <MessageCircle className="h-4 w-4 text-primary" />
        <span className="flex-1 text-sm font-semibold">Chat with {otherPartyName}</span>
        {recipientId && (
          <div className="relative">
            <button
              type="button"
              aria-label="Safety options"
              onClick={() => setSafetyMenuOpen((v) => !v)}
              className="rounded-full p-1 text-muted-foreground hover:bg-muted"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {safetyMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setSafetyMenuOpen(false)} />
                <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-lg border bg-background shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setSafetyMenuOpen(false);
                      setReportOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    <Flag className="h-4 w-4" /> Report
                  </button>
                  <button
                    type="button"
                    onClick={handleBlock}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-muted"
                  >
                    <Ban className="h-4 w-4" /> Block
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {recipientId && (
        <ReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          targetType="user"
          targetId={recipientId}
          targetLabel={otherPartyName}
        />
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex flex-col gap-2 overflow-y-auto px-4 py-3"
        style={{ maxHeight: "320px", minHeight: "120px" }}
      >
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading messages...
          </div>
        )}

        {!isLoading && messages.length === 0 && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            No messages yet. Start the conversation!
          </div>
        )}

        {messages.map((msg, idx) => {
          const isOwn = msg.senderId === userId;
          return (
            <div
              key={msg.id}
              className={`flex flex-col max-w-[80%] ${isOwn ? "self-end items-end" : "self-start items-start"}`}
            >
              <span className="text-[10px] text-muted-foreground mb-0.5 px-1">
                {isOwn ? "You" : msg.senderName || otherPartyName}
              </span>
              <div
                className={`rounded-xl px-3 py-2 text-sm ${
                  isOwn
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                {msg.imageUrl && isSafeImageUrl(msg.imageUrl) && (
                  <a
                    href={msg.imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img
                      src={msg.imageUrl}
                      alt="Shared image"
                      className="max-w-[12rem] rounded-lg"
                      loading="lazy"
                    />
                  </a>
                )}
                {msg.body && (
                  <div className={msg.imageUrl ? "mt-1.5" : undefined}>{msg.body}</div>
                )}
              </div>
              <div className="mt-0.5 px-1">
                <MessageReactions
                  reactions={msg.reactions}
                  currentUserId={userId}
                  onReact={(emoji) => reactMutation.mutate({ messageId: msg.id, emoji })}
                  align={isOwn ? "end" : "start"}
                />
              </div>
              <span className="text-[10px] text-muted-foreground mt-0.5 px-1">
                {formatTime(msg.createdAt)}
                {idx === lastOwnIndex && (
                  <span className="ml-1">{msg.isRead ? "· Read" : "· Sent"}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 border-t px-3 py-2.5">
        {allowImages && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelected}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              disabled={isUploading || sendMutation.isPending}
              onClick={() => fileRef.current?.click()}
              aria-label="Attach image"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="h-4 w-4" />
              )}
            </Button>
          </>
        )}
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type a message..."
          className="flex-1 text-sm"
        />
        <Button
          type="button"
          size="icon"
          disabled={!text.trim() || sendMutation.isPending}
          onClick={handleSend}
          aria-label="Send message"
        >
          {sendMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
